import React from "react";

const MDM_TYPE_LABELS = {
  MINOR: "Minor",
  CHRONIC_STABLE: "Chronic, Stable",
  CHRONIC_PROGRESSION: "Chronic, Progression",
  CHRONIC_SEVERE: "Chronic, Severe",
  ACUTE_UNCOMPLICATED: "Acute, Uncomplicated",
  ACUTE_COMPLICATED: "Acute, Complicated",
  ACUTE_SYSTEMIC: "Acute, Systemic",
  UNDIAGNOSED: "Undiagnosed",
  LIFE_THREAT: "Life Threatening",
  REVIEW_NOTE: "Review Note",
  REVIEW_RESULT: "Review Test Result",
  ORDER_TEST: "Order Test",
  HISTORIAN: "Historian",
  EXTERNAL_DISCUSSION: "External Discussion",
  EXTERNAL_INTERPRETATION: "External Interpretation",
};

const labelForType = (type) => MDM_TYPE_LABELS[type] || String(type || "-");

const formatCode = (code) => (code ? String(code) : "—");

const formatVisitType = (visitType) =>
  visitType === "new" ? "New patient" : "Established patient";

const formatBasis = (basis) =>
  basis === "time" ? "Based on time spent" : "Based on medical decision making";

function LevelBadge({ level, highlighted = false }) {
  return (
    <span
      className={`inline-flex min-w-[64px] items-center justify-center rounded-full px-3 py-1 text-xs font-bold ${
        highlighted
          ? "bg-ktx-purple text-white"
          : "bg-barely-purple text-ktx-purple"
      }`}
    >
      Level {level ?? "-"}
    </span>
  );
}

function MdmCard({ title, level, highlighted, children }) {
  return (
    <div className="flex min-h-0 flex-col rounded-xl border border-gray-200 bg-gray-50 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-ktx-navy">{title}</p>
        <LevelBadge level={level} highlighted={highlighted} />
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

function MdmItem({ type, title, detail }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white p-2 text-xs">
      <p className="font-semibold text-gray-500">{labelForType(type)}</p>
      {title && <p className="mt-1 text-sm font-semibold text-ktx-navy">{title}</p>}
      {detail && <p className="mt-1 text-gray-600">{detail}</p>}
    </div>
  );
}

function Icd10List({ title, codes }) {
  const list = Array.isArray(codes) ? codes : [];
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
      <p className="mb-2 text-sm font-semibold text-ktx-navy">
        {title} ({list.length})
      </p>
      {list.length === 0 ? (
        <p className="text-xs text-ktx-navy/70">None</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {list.map((entry) => (
            <li
              key={entry.code}
              className="flex items-start gap-2 rounded-md bg-white px-2 py-1 text-sm"
            >
              <span className="shrink-0 font-mono font-semibold text-ktx-purple">
                {entry.code}
              </span>
              <span className="text-ktx-navy">{entry.name || ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function CodingPanel({ coding, className = "" }) {
  if (!coding || typeof coding !== "object") {
    return null;
  }

  const eAndM = coding.e_and_m || {};
  const recommended = eAndM.recommended || {};
  const provider = eAndM.provider || {};
  const mdm = eAndM.mdm || {};
  const time = eAndM.time || {};
  const icd10 = coding.icd10 || {};
  const mdmLevels = [mdm.problems?.level, mdm.data?.level, mdm.risk?.level];
  const sortedLevels = [...mdmLevels].sort((a, b) => a - b);
  const middleLevel = sortedLevels[1];
  const useTime = recommended.basis === "time";
  const providerCode = provider.final_code;
  const codesDiffer = Boolean(
    providerCode && recommended.code && providerCode !== recommended.code,
  );
  const providerSelected = Boolean(providerCode || provider.final_level);

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className="rounded-xl bg-white p-4 shadow-knowtex-shadow">
        <div className="rounded-xl bg-dark-purple px-4 py-3 text-white">
          <h2 className="text-[20px]">E&M Coding</h2>
          <p className="text-xs text-white/80">
            {formatVisitType(eAndM.visit_type)}
          </p>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-ktx-purple/30 bg-barely-purple p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-ktx-navy/70">
              Knowtex recommendation
            </p>
            <p className="mt-1 text-3xl font-black text-ktx-purple">
              {formatCode(recommended.code)}
            </p>
            <p className="text-sm font-medium text-ktx-navy">
              Level {recommended.level ?? "-"} · {formatBasis(recommended.basis)}
            </p>
            {eAndM.model_code && eAndM.model_code !== recommended.code && (
              <p className="mt-1 text-xs text-ktx-navy/70">
                Model output: {eAndM.model_code}
              </p>
            )}
          </div>

          <div
            className={`rounded-xl border p-4 ${
              codesDiffer
                ? "border-amber-400 bg-amber-50"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-ktx-navy/70">
                Provider selection
              </p>
              {codesDiffer && (
                <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-bold text-white">
                  Differs
                </span>
              )}
            </div>
            <p className="mt-1 text-3xl font-black text-ktx-navy">
              {providerSelected ? formatCode(providerCode) : "Not selected"}
            </p>
            {providerSelected && (
              <p className="text-sm font-medium text-ktx-navy">
                Level {provider.final_level ?? "-"}
                {provider.original_level != null &&
                provider.original_level !== provider.final_level
                  ? ` (first picked ${provider.original_level})`
                  : ""}
              </p>
            )}
            {provider.override_rationale && (
              <div className="mt-2 rounded-md bg-white p-2 text-xs text-ktx-navy">
                <p className="font-semibold text-gray-500">Override rationale</p>
                <p className="mt-1 whitespace-pre-wrap">{provider.override_rationale}</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-ktx-navy/80">
          <span className="font-semibold">MDM level is the middle of three:</span>
          <span>
            problems {mdm.problems?.level ?? "-"}, data {mdm.data?.level ?? "-"}, risk{" "}
            {mdm.risk?.level ?? "-"} → {middleLevel ?? "-"}
          </span>
          <span className="text-ktx-navy/50">|</span>
          <span>
            Time {time.minutes != null ? `${time.minutes} min` : "not recorded"}
            {time.level != null ? ` → level ${time.level}` : ""}
            {useTime ? " (used, higher than MDM)" : ""}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
          <MdmCard
            title="Problems"
            level={mdm.problems?.level}
            highlighted={!useTime && mdm.problems?.level === middleLevel}
          >
            {(mdm.problems?.items || []).length === 0 ? (
              <p className="text-xs text-ktx-navy/70">No problems listed.</p>
            ) : (
              mdm.problems.items.map((item, index) => (
                <MdmItem
                  key={`${item.type}-${index}`}
                  type={item.type}
                  title={item.name}
                  detail={item.details}
                />
              ))
            )}
          </MdmCard>
          <MdmCard
            title="Data"
            level={mdm.data?.level}
            highlighted={!useTime && mdm.data?.level === middleLevel}
          >
            {(mdm.data?.items || []).length === 0 ? (
              <p className="text-xs text-ktx-navy/70">No data items listed.</p>
            ) : (
              mdm.data.items.map((item, index) => (
                <MdmItem
                  key={`${item.type}-${index}`}
                  type={item.type}
                  title={item.name}
                  detail={item.context}
                />
              ))
            )}
          </MdmCard>
          <MdmCard
            title="Risk"
            level={mdm.risk?.level}
            highlighted={!useTime && mdm.risk?.level === middleLevel}
          >
            <MdmItem
              type={mdm.risk?.type || "-"}
              title="Risk rationale"
              detail={mdm.risk?.rationale || "No rationale provided."}
            />
          </MdmCard>
        </div>
      </div>

      <div className="rounded-xl bg-white p-4 shadow-knowtex-shadow">
        <div className="rounded-xl bg-dark-purple px-4 py-3 text-white">
          <h2 className="text-[20px]">ICD-10 Codes</h2>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <Icd10List title="Primary" codes={icd10.primary} />
          <Icd10List title="Secondary" codes={icd10.secondary} />
        </div>
        {Array.isArray(icd10.historical) && icd10.historical.length > 0 && (
          <p className="mt-3 text-xs text-ktx-navy/70">
            Historical codes (not reviewed): {icd10.historical.join(", ")}
          </p>
        )}
      </div>
    </div>
  );
}
